import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  BookOpen, Users, Award, DollarSign, Settings, Plus, Copy,
  Loader2, Trash2, Edit, Save, X, Star, StarOff, BarChart3,
  FileText, ChevronDown, ChevronRight, GraduationCap, TrendingUp,
  GripVertical, FolderPlus, FilePlus, ArrowUp, ArrowDown, Layers, Upload
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== COURSE FORM DIALOG =====
const CourseFormDialog = ({ open, onClose, onSave, course, categories }) => {
  const [form, setForm] = useState({
    title: '', description: '', category_id: '', level: 'beginner',
    thumbnail_url: '', is_published: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (course) {
      setForm({
        title: course.title || '',
        description: course.description || '',
        category_id: course.category_id || '',
        level: course.level || 'beginner',
        thumbnail_url: course.thumbnail_url || '',
        is_published: course.is_published !== false
      });
    } else {
      setForm({ title: '', description: '', category_id: '', level: 'beginner', thumbnail_url: '', is_published: true });
    }
  }, [course, open]);

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="course-form-dialog">
        <DialogHeader>
          <DialogTitle>{course ? 'Edit Course' : 'Create New Course'}</DialogTitle>
          <DialogDescription>
            {course ? 'Update course details below.' : 'Fill in the details to create a new course.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Title *</label>
            <Input data-testid="course-title-input" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. ChatGPT Mastery" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Description</label>
            <Textarea data-testid="course-desc-input" value={form.description} rows={3}
              onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Course description..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Category</label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger data-testid="course-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Level</label>
              <Select value={form.level} onValueChange={(v) => setForm({ ...form, level: v })}>
                <SelectTrigger data-testid="course-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Thumbnail URL</label>
            <Input value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })}
              placeholder="https://..." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="course-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {course ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===== LESSON EDITOR DIALOG =====
const LessonEditorDialog = ({ open, onClose, onSave, lesson }) => {
  const [form, setForm] = useState({ title: '', content: '', duration: 15, checklist_text: '', audio_script: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lesson) {
      setForm({
        title: lesson.title || '', content: lesson.content || '',
        duration: lesson.duration || 15, checklist_text: lesson.checklist_text || '',
        audio_script: lesson.audio_script || ''
      });
    } else {
      setForm({ title: '', content: '', duration: 15, checklist_text: '', audio_script: '' });
    }
  }, [lesson, open]);

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save lesson');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lesson-editor-dialog">
        <DialogHeader>
          <DialogTitle>{lesson ? 'Edit Lesson' : 'Add New Lesson'}</DialogTitle>
          <DialogDescription>
            {lesson ? 'Update lesson content below.' : 'Create a new lesson for this module.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-sm font-medium text-slate-700 block mb-1">Title *</label>
              <Input data-testid="lesson-title-input" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">Duration (min)</label>
              <Input type="number" value={form.duration}
                onChange={(e) => setForm({ ...form, duration: parseInt(e.target.value) || 15 })} />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Content (Markdown)</label>
            <Textarea data-testid="lesson-content-input" value={form.content} rows={10}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Lesson content in markdown format..." className="font-mono text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Checklist Text</label>
            <Textarea value={form.checklist_text} rows={4}
              onChange={(e) => setForm({ ...form, checklist_text: e.target.value })}
              placeholder="Downloadable checklist content..." className="font-mono text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Audio Script (TTS)</label>
            <Textarea value={form.audio_script} rows={4}
              onChange={(e) => setForm({ ...form, audio_script: e.target.value })}
              placeholder="Script for text-to-speech narration..." className="font-mono text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="lesson-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {lesson ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===== CATEGORY FORM DIALOG =====
const CategoryFormDialog = ({ open, onClose, onSave, category }) => {
  const [form, setForm] = useState({ name: '', icon: 'BookOpen', description: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (category) {
      setForm({ name: category.name || '', icon: category.icon || 'BookOpen', description: category.description || '' });
    } else {
      setForm({ name: '', icon: 'BookOpen', description: '' });
    }
  }, [category, open]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save category');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="category-form-dialog">
        <DialogHeader>
          <DialogTitle>{category ? 'Edit Category' : 'Create New Category'}</DialogTitle>
          <DialogDescription>
            {category ? 'Update category details.' : 'Add a new course category.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Name *</label>
            <Input data-testid="category-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Marketing" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Icon Name</label>
            <Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}
              placeholder="e.g. BookOpen, Video, Palette" />
            <p className="text-xs text-slate-400 mt-1">Lucide icon name (e.g. BookOpen, Video, Palette, Zap)</p>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1">Description</label>
            <Textarea value={form.description} rows={2}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="category-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            {category ? 'Update' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ===== MODULE MANAGEMENT WITHIN A COURSE =====
const CourseModulesManager = ({ course, headers, onRefresh }) => {
  const [expandedModule, setExpandedModule] = useState(null);
  const [addingModule, setAddingModule] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState('');
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [editModuleTitle, setEditModuleTitle] = useState('');
  const [lessonDialog, setLessonDialog] = useState({ open: false, lesson: null, moduleId: null });
  const [loadingLessonContent, setLoadingLessonContent] = useState(null);
  const [jsonImportOpen, setJsonImportOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonImporting, setJsonImporting] = useState(false);
  const [jsonReplace, setJsonReplace] = useState(false);
  const jsonFileRef = React.useRef(null);

  const addModule = async () => {
    if (!newModuleTitle.trim()) return;
    try {
      await axios.post(`${API}/academy/admin/courses/${course.id}/modules`, { title: newModuleTitle }, { headers });
      toast.success('Module added');
      setAddingModule(false);
      setNewModuleTitle('');
      onRefresh();
    } catch (err) { toast.error('Failed to add module'); }
  };

  const updateModule = async (moduleId) => {
    try {
      await axios.put(`${API}/academy/admin/courses/${course.id}/modules/${moduleId}`, { title: editModuleTitle }, { headers });
      toast.success('Module updated');
      setEditingModuleId(null);
      onRefresh();
    } catch (err) { toast.error('Failed to update module'); }
  };

  const deleteModule = async (moduleId, title) => {
    if (!window.confirm(`Delete module "${title}" and all its lessons?`)) return;
    try {
      await axios.delete(`${API}/academy/admin/courses/${course.id}/modules/${moduleId}`, { headers });
      toast.success('Module deleted');
      onRefresh();
    } catch (err) { toast.error('Failed to delete module'); }
  };

  const moveModule = async (moduleId, direction) => {
    const ids = course.modules.map(m => m.id);
    const idx = ids.indexOf(moduleId);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === ids.length - 1)) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [ids[idx], ids[swapIdx]] = [ids[swapIdx], ids[idx]];
    try {
      await axios.put(`${API}/academy/admin/courses/${course.id}/modules/reorder`, { module_ids: ids }, { headers });
      onRefresh();
    } catch (err) { toast.error('Failed to reorder'); }
  };

  const openEditLesson = async (lesson, moduleId) => {
    if (!lesson.content && lesson.id) {
      setLoadingLessonContent(lesson.id);
      try {
        const res = await axios.get(`${API}/academy/lesson/${lesson.id}`, { headers });
        setLessonDialog({ open: true, lesson: { ...lesson, content: res.data.content || '', checklist_text: res.data.checklist_text || '', audio_script: res.data.audio_script || '' }, moduleId });
      } catch {
        setLessonDialog({ open: true, lesson, moduleId });
      } finally { setLoadingLessonContent(null); }
    } else {
      setLessonDialog({ open: true, lesson, moduleId });
    }
  };

  const saveLesson = async (formData) => {
    if (lessonDialog.lesson?.id) {
      await axios.put(`${API}/academy/admin/lesson/${lessonDialog.lesson.id}`, formData, { headers });
      toast.success('Lesson updated');
    } else {
      await axios.post(`${API}/academy/admin/courses/${course.id}/modules/${lessonDialog.moduleId}/lessons`, formData, { headers });
      toast.success('Lesson added');
    }
    onRefresh();
  };

  const deleteLesson = async (lessonId, title) => {
    if (!window.confirm(`Delete lesson "${title}"?`)) return;
    try {
      await axios.delete(`${API}/academy/admin/lesson/${lessonId}`, { headers });
      toast.success('Lesson deleted');
      onRefresh();
    } catch (err) { toast.error('Failed to delete lesson'); }
  };

  const moveLesson = async (moduleId, lessonId, direction) => {
    const mod = course.modules.find(m => m.id === moduleId);
    if (!mod) return;
    const ids = mod.lessons.map(l => l.id);
    const idx = ids.indexOf(lessonId);
    if ((direction === 'up' && idx === 0) || (direction === 'down' && idx === ids.length - 1)) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [ids[idx], ids[swapIdx]] = [ids[swapIdx], ids[idx]];
    try {
      await axios.put(`${API}/academy/admin/courses/${course.id}/modules/${moduleId}/lessons/reorder`, { lesson_ids: ids }, { headers });
      onRefresh();
    } catch (err) { toast.error('Failed to reorder'); }
  };

  const handleJsonFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setJsonText(ev.target.result);
      setJsonImportOpen(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const importJson = async () => {
    if (!jsonText.trim()) { toast.error('Please paste or upload JSON content'); return; }
    setJsonImporting(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/courses/${course.id}/import-json`,
        { json_content: jsonText, replace: jsonReplace },
        { headers }
      );
      toast.success(res.data.message || 'Import successful');
      setJsonImportOpen(false);
      setJsonText('');
      setJsonReplace(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setJsonImporting(false); }
  };

  return (
    <div className="mt-4 border-t pt-4 space-y-2" data-testid="course-modules-manager">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
          <Layers className="w-4 h-4" /> Modules & Lessons
        </h4>
        <div className="flex gap-2">
          <input type="file" ref={jsonFileRef} accept=".json" className="hidden" onChange={handleJsonFileUpload} />
          <Button size="sm" variant="outline" onClick={() => setJsonImportOpen(true)} data-testid="import-json-btn"
            className="text-indigo-600 border-indigo-300 hover:bg-indigo-50">
            <Upload className="w-3.5 h-3.5 mr-1" /> Import JSON
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAddingModule(true)} data-testid="add-module-btn">
            <FolderPlus className="w-3.5 h-3.5 mr-1" /> Add Module
          </Button>
        </div>
      </div>

      {/* JSON Import Dialog */}
      {jsonImportOpen && (
        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg mb-3 space-y-3" data-testid="json-import-dialog">
          <div className="flex items-center justify-between">
            <h5 className="font-semibold text-sm text-indigo-900 flex items-center gap-1.5">
              <Upload className="w-4 h-4" /> Import Modules from JSON
            </h5>
            <Button size="sm" variant="ghost" onClick={() => { setJsonImportOpen(false); setJsonText(''); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-indigo-700">
            Upload a JSON file or paste JSON below. Supports structures with "modules", "sections", "chapters", or an array of modules. Each module should have a title and lessons array.
          </p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => jsonFileRef.current?.click()}>
              <Upload className="w-3.5 h-3.5 mr-1" /> Upload .json File
            </Button>
          </div>
          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            placeholder={'Paste your JSON here...\n\nExample:\n{\n  "modules": [\n    {\n      "title": "Module 1",\n      "lessons": [\n        { "title": "Lesson 1", "content": "..." }\n      ]\n    }\n  ]\n}'}
            rows={8}
            className="w-full p-3 text-xs font-mono border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-y"
            data-testid="json-import-textarea"
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={jsonReplace} onChange={(e) => setJsonReplace(e.target.checked)} className="rounded" />
              Replace existing modules (instead of appending)
            </label>
            <Button size="sm" onClick={importJson} disabled={jsonImporting || !jsonText.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="json-import-submit-btn">
              {jsonImporting ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Importing...</> : <><Upload className="w-3.5 h-3.5 mr-1" /> Import All Modules</>}
            </Button>
          </div>
        </div>
      )}

      {addingModule && (
        <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg mb-2">
          <Input value={newModuleTitle} onChange={(e) => setNewModuleTitle(e.target.value)}
            placeholder="Module title" className="flex-1" data-testid="new-module-title-input"
            onKeyDown={(e) => e.key === 'Enter' && addModule()} />
          <Button size="sm" onClick={addModule}><Save className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant="ghost" onClick={() => setAddingModule(false)}><X className="w-3.5 h-3.5" /></Button>
        </div>
      )}

      {course.modules?.map((mod, mIdx) => (
        <div key={mod.id} className="border rounded-lg overflow-hidden" data-testid={`module-${mIdx}`}>
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors">
            <button onClick={() => setExpandedModule(expandedModule === mod.id ? null : mod.id)}
              className="flex items-center gap-2 flex-1 text-left">
              {expandedModule === mod.id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
              {editingModuleId === mod.id ? (
                <div className="flex items-center gap-2 flex-1" onClick={(e) => e.stopPropagation()}>
                  <Input value={editModuleTitle} onChange={(e) => setEditModuleTitle(e.target.value)}
                    className="h-7 text-sm flex-1" onKeyDown={(e) => e.key === 'Enter' && updateModule(mod.id)} />
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => updateModule(mod.id)}><Save className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingModuleId(null)}><X className="w-3 h-3" /></Button>
                </div>
              ) : (
                <>
                  <span className="text-sm font-medium text-slate-800">Module {mIdx + 1}: {mod.title}</span>
                  <Badge variant="outline" className="ml-auto text-xs">{mod.lessons?.length || 0} lessons</Badge>
                </>
              )}
            </button>
            {editingModuleId !== mod.id && (
              <div className="flex items-center gap-0.5">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => moveModule(mod.id, 'up')} disabled={mIdx === 0}>
                  <ArrowUp className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => moveModule(mod.id, 'down')} disabled={mIdx === course.modules.length - 1}>
                  <ArrowDown className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingModuleId(mod.id); setEditModuleTitle(mod.title); }}>
                  <Edit className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => deleteModule(mod.id, mod.title)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>

          {expandedModule === mod.id && (
            <div className="px-3 py-2 space-y-1 bg-white">
              {mod.lessons?.map((les, lIdx) => (
                <div key={les.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 group text-sm"
                  data-testid={`lesson-${mIdx}-${lIdx}`}>
                  <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                  <span className="text-slate-500 w-8">{mIdx + 1}.{lIdx + 1}</span>
                  <span className="flex-1 text-slate-700 truncate">{les.title}</span>
                  <span className="text-xs text-slate-400">{les.duration}m</span>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveLesson(mod.id, les.id, 'up')} disabled={lIdx === 0}>
                      <ArrowUp className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveLesson(mod.id, les.id, 'down')} disabled={lIdx === mod.lessons.length - 1}>
                      <ArrowDown className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openEditLesson(les, mod.id)}
                      disabled={loadingLessonContent === les.id}>
                      {loadingLessonContent === les.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Edit className="w-3 h-3" />}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                      onClick={() => deleteLesson(les.id, les.title)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button size="sm" variant="ghost" className="w-full justify-center text-slate-500 hover:text-primary mt-1 border border-dashed border-slate-200"
                onClick={() => setLessonDialog({ open: true, lesson: null, moduleId: mod.id })}
                data-testid={`add-lesson-btn-${mIdx}`}>
                <FilePlus className="w-3.5 h-3.5 mr-1" /> Add Lesson
              </Button>
            </div>
          )}
        </div>
      ))}

      <LessonEditorDialog
        open={lessonDialog.open}
        onClose={() => setLessonDialog({ open: false, lesson: null, moduleId: null })}
        onSave={saveLesson}
        lesson={lessonDialog.lesson}
      />
    </div>
  );
};


// ===== MAIN COMPONENT =====
const AcademyAdmin = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [members, setMembers] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [pricingForm, setPricingForm] = useState({ monthly: '', yearly: '' });
  const [savingPricing, setSavingPricing] = useState(false);

  // Dialog states
  const [courseDialog, setCourseDialog] = useState({ open: false, course: null });
  const [categoryDialog, setCategoryDialog] = useState({ open: false, category: null });

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    loadData();
  }, [user]);

  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      const [statsRes, coursesRes, membersRes, pricingRes, catsRes] = await Promise.all([
        axios.get(`${API}/academy/admin/stats`, { headers }),
        axios.get(`${API}/academy/admin/courses`, { headers }),
        axios.get(`${API}/academy/admin/members`, { headers }),
        axios.get(`${API}/academy/admin/pricing`, { headers }),
        axios.get(`${API}/academy/admin/categories`, { headers })
      ]);
      setStats(statsRes.data);
      setCourses(coursesRes.data);
      setMembers(membersRes.data);
      setPricing(pricingRes.data);
      setCategories(catsRes.data);
      setPricingForm({
        monthly: pricingRes.data.monthly?.price || 17,
        yearly: pricingRes.data.yearly?.price || 170
      });
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally { setLoading(false); }
  }, [token]);

  // Course operations
  const saveCourse = async (formData) => {
    if (courseDialog.course) {
      await axios.put(`${API}/academy/admin/courses/${courseDialog.course.id}`, formData, { headers });
      toast.success('Course updated');
    } else {
      await axios.post(`${API}/academy/admin/courses`, formData, { headers });
      toast.success('Course created');
    }
    loadData();
  };

  const deleteCourse = async (courseId, title) => {
    if (!window.confirm(`Delete "${title}"? This removes ALL lessons, quizzes, progress, and certificates. Cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/academy/admin/courses/${courseId}`, { headers });
      toast.success('Course deleted');
      if (expandedCourse === courseId) setExpandedCourse(null);
      loadData();
    } catch (err) { toast.error('Failed to delete course'); }
  };

  const duplicateCourse = async (courseId) => {
    try {
      const res = await axios.post(`${API}/academy/admin/courses/${courseId}/duplicate`, {}, { headers });
      toast.success(res.data.message || 'Course duplicated');
      loadData();
    } catch (err) { toast.error('Failed to duplicate course'); }
  };

  const toggleFeatured = async (courseId) => {
    try {
      const res = await axios.put(`${API}/academy/admin/courses/${courseId}/featured`, {}, { headers });
      toast.success(res.data.message);
      loadData();
    } catch (err) { toast.error('Failed to update featured status'); }
  };

  // Category operations
  const saveCategory = async (formData) => {
    if (categoryDialog.category) {
      await axios.put(`${API}/academy/admin/categories/${categoryDialog.category.id}`, formData, { headers });
      toast.success('Category updated');
    } else {
      await axios.post(`${API}/academy/admin/categories`, formData, { headers });
      toast.success('Category created');
    }
    loadData();
  };

  const deleteCategory = async (catId, name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try {
      await axios.delete(`${API}/academy/admin/categories/${catId}`, { headers });
      toast.success('Category deleted');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete category'); }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      await axios.put(`${API}/academy/admin/pricing`, {
        monthly: parseFloat(pricingForm.monthly),
        yearly: parseFloat(pricingForm.yearly)
      }, { headers });
      toast.success('Pricing updated');
      loadData();
    } catch (err) { toast.error('Failed to update pricing'); }
    finally { setSavingPricing(false); }
  };

  const getCategoryName = (catId) => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.name : catId;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="academy-admin-page">
      <Navbar />

      <div className="pt-16 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-7 h-7 text-primary" />
              <div>
                <h1 className="font-heading text-2xl font-bold">AI Academy Admin</h1>
                <p className="text-slate-400">Manage courses, categories, members & pricing</p>
              </div>
            </div>
            <Button onClick={() => setCourseDialog({ open: true, course: null })}
              className="bg-primary hover:bg-primary/90" data-testid="create-course-btn">
              <Plus className="w-4 h-4 mr-1" /> New Course
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8" data-testid="admin-stats">
            {[
              { label: 'Courses', value: stats.total_courses, icon: BookOpen, color: 'bg-blue-100 text-blue-600' },
              { label: 'Lessons', value: stats.total_lessons, icon: FileText, color: 'bg-green-100 text-green-600' },
              { label: 'Members', value: stats.total_members, icon: Users, color: 'bg-purple-100 text-purple-600' },
              { label: 'Certificates', value: stats.total_certificates, icon: Award, color: 'bg-yellow-100 text-yellow-600' },
              { label: 'Est. MRR', value: `$${stats.estimated_mrr}`, icon: DollarSign, color: 'bg-emerald-100 text-emerald-600' }
            ].map(({ label, value, icon: Icon, color }) => (
              <Card key={label}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{value}</p>
                    <p className="text-xs text-slate-500">{label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="courses">
          <TabsList className="mb-6">
            <TabsTrigger value="courses" data-testid="tab-courses">
              <BookOpen className="w-4 h-4 mr-2" /> Courses ({courses.length})
            </TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">
              <Layers className="w-4 h-4 mr-2" /> Categories ({categories.length})
            </TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-members">
              <Users className="w-4 h-4 mr-2" /> Members ({members.length})
            </TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">
              <DollarSign className="w-4 h-4 mr-2" /> Pricing
            </TabsTrigger>
          </TabsList>

          {/* ===== COURSES TAB ===== */}
          <TabsContent value="courses">
            <div className="space-y-3">
              {courses.length === 0 ? (
                <Card className="p-8 text-center">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-1">No courses yet</h3>
                  <p className="text-sm text-slate-600 mb-4">Create your first course to get started.</p>
                  <Button onClick={() => setCourseDialog({ open: true, course: null })}>
                    <Plus className="w-4 h-4 mr-1" /> Create Course
                  </Button>
                </Card>
              ) : courses.map((course) => (
                <Card key={course.id} className={`overflow-hidden transition-all ${course.is_featured ? 'ring-2 ring-yellow-400' : ''}`}
                  data-testid={`admin-course-${course.slug}`}>
                  <div className="p-4">
                    <div className="flex items-start gap-4">
                      {course.thumbnail_url && (
                        <img src={course.thumbnail_url} alt={course.title}
                          className="w-20 h-14 object-cover rounded-lg flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold text-slate-900 truncate">{course.title}</h3>
                          {course.is_featured && (
                            <Badge className="bg-yellow-100 text-yellow-700 border-yellow-300 text-xs">
                              <Star className="w-3 h-3 mr-0.5 fill-yellow-500" /> Featured
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">{getCategoryName(course.category_id)}</Badge>
                          <Badge className="text-xs bg-slate-100 text-slate-600">{course.level}</Badge>
                          {course.is_published === false && (
                            <Badge className="bg-red-100 text-red-600 text-xs">Draft</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-1">{course.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span>{course.total_modules || 0} modules</span>
                          <span>{course.total_lessons || 0} lessons</span>
                          <span>{course.enrolled_count || 0} enrolled</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button size="sm" variant="ghost" title={course.is_featured ? 'Remove featured' : 'Set as Course of the Week'}
                          onClick={() => toggleFeatured(course.id)} data-testid={`featured-btn-${course.slug}`}>
                          {course.is_featured ? <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" /> : <StarOff className="w-4 h-4 text-slate-400" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}
                          data-testid={`expand-course-${course.slug}`}>
                          {expandedCourse === course.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setCourseDialog({ open: true, course })}
                          data-testid={`edit-course-${course.slug}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Duplicate" onClick={() => duplicateCourse(course.id)}
                          data-testid={`duplicate-course-${course.slug}`}>
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => deleteCourse(course.id, course.title)}
                          data-testid={`delete-course-${course.slug}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {expandedCourse === course.id && (
                      <CourseModulesManager course={course} headers={headers} onRefresh={loadData} />
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ===== CATEGORIES TAB ===== */}
          <TabsContent value="categories">
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => setCategoryDialog({ open: true, category: null })} data-testid="create-category-btn">
                  <Plus className="w-4 h-4 mr-1" /> New Category
                </Button>
              </div>
              {categories.length === 0 ? (
                <Card className="p-8 text-center">
                  <Layers className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-1">No categories yet</h3>
                  <p className="text-sm text-slate-600">Categories help organize your courses.</p>
                </Card>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categories.map((cat) => (
                    <Card key={cat.id} data-testid={`category-card-${cat.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-900">{cat.name}</h3>
                            {cat.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{cat.description}</p>}
                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                              <span>Icon: {cat.icon}</span>
                              <Badge variant="outline">{cat.course_count || 0} courses</Badge>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setCategoryDialog({ open: true, category: cat })}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700"
                              onClick={() => deleteCategory(cat.id, cat.name)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ===== MEMBERS TAB ===== */}
          <TabsContent value="members">
            {members.length === 0 ? (
              <Card className="p-8 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1">No members yet</h3>
                <p className="text-sm text-slate-600">Members will appear here when users subscribe.</p>
              </Card>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-3 font-medium text-slate-600">Member</th>
                        <th className="text-left p-3 font-medium text-slate-600">Plan</th>
                        <th className="text-left p-3 font-medium text-slate-600">Status</th>
                        <th className="text-left p-3 font-medium text-slate-600">Courses</th>
                        <th className="text-left p-3 font-medium text-slate-600">Certificates</th>
                        <th className="text-left p-3 font-medium text-slate-600">Joined</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {members.map((m) => (
                        <tr key={m.user_id} className="hover:bg-slate-50">
                          <td className="p-3">
                            <p className="font-medium text-slate-900">{m.user_name}</p>
                            <p className="text-xs text-slate-500">{m.user_email}</p>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="capitalize">{m.plan || 'N/A'}</Badge>
                          </td>
                          <td className="p-3">
                            <Badge className={m.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {m.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-slate-600">{m.courses_started}</td>
                          <td className="p-3 text-slate-600">{m.certificates_earned}</td>
                          <td className="p-3 text-xs text-slate-500">
                            {m.created_at ? new Date(m.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ===== PRICING TAB ===== */}
          <TabsContent value="pricing">
            <div className="max-w-lg">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" /> Membership Pricing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Monthly Price ($)</label>
                    <Input type="number" step="0.01" value={pricingForm.monthly}
                      onChange={(e) => setPricingForm({ ...pricingForm, monthly: e.target.value })}
                      data-testid="pricing-monthly" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Yearly Price ($)</label>
                    <Input type="number" step="0.01" value={pricingForm.yearly}
                      onChange={(e) => setPricingForm({ ...pricingForm, yearly: e.target.value })}
                      data-testid="pricing-yearly" />
                    <p className="text-xs text-slate-500 mt-1">
                      That's ${(pricingForm.yearly / 12).toFixed(2)}/month — saves ${((pricingForm.monthly * 12) - pricingForm.yearly).toFixed(0)} vs monthly
                    </p>
                  </div>
                  <Button onClick={savePricing} disabled={savingPricing} data-testid="save-pricing-btn">
                    {savingPricing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Pricing
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <CourseFormDialog
        open={courseDialog.open}
        onClose={() => setCourseDialog({ open: false, course: null })}
        onSave={saveCourse}
        course={courseDialog.course}
        categories={categories}
      />
      <CategoryFormDialog
        open={categoryDialog.open}
        onClose={() => setCategoryDialog({ open: false, category: null })}
        onSave={saveCategory}
        category={categoryDialog.category}
      />
    </div>
  );
};

export default AcademyAdmin;
